import type { Declaration } from "../../../../../api/types"

/**
 * The next declaration Studio V2's "Überspringen" (and the post-approve
 * success screen's "onDone") jump to — the SAME predicate v1's
 * `DeclarationReviewPage::otherPending` filters on (`lifecycleStatus ===
 * "ready"`, excluding the one currently open and held copies of our own
 * inquiry files, TUR-1754), just returning the first match's id instead of a
 * capped list.
 *
 * SESSION-ONLY: a skipped declaration is not marked deferred anywhere —
 * reloading the list surfaces it again at its normal place. Persisting a
 * defer is a known product gap (plan §F.3), not attempted here.
 *
 * Because nothing is persisted, a skipped row stays `ready` in the list. So
 * the search starts AFTER the current row's position and wraps around, not at
 * index 0: restarting at the top sent the reviewer straight back to the
 * declaration they had just skipped (A -> B -> A, never C).
 *
 * `null` DOES NOT MEAN "nothing left" — it only means no `ready` row is
 * currently LOADED besides the one just left. Two very different things
 * produce it: the reviewer genuinely finished (approved/requested this
 * declaration and nothing else loaded is `ready`), or Überspringen skipped a
 * declaration that is still `ready` server-side and just happens to be the
 * only loaded `ready` row (or the next one sits beyond the loaded page —
 * `declarations` here is a page, never the server's full set). Callers must
 * not collapse that distinction into one "all done" message — see
 * `StudioV2DoneScreen`'s `reason` prop, which exists for exactly this.
 */
export function nextOpenDeclaration(
  declarations: readonly Pick<
    Declaration,
    "id" | "lifecycleStatus" | "roundtripHold"
  >[],
  currentId: string,
): string | null {
  const start = declarations.findIndex((d) => d.id === currentId) + 1
  const n = declarations.length
  for (let i = 0; i < n; i++) {
    const d = declarations[(start + i) % n]
    if (d.id !== currentId && d.lifecycleStatus === "ready" && !d.roundtripHold)
      return d.id
  }
  return null
}
